import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import CurrencyPicker from "@/components/CurrencyPicker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/book", label: "Book" },
    { to: "/collab", label: "Collaborate" },
    { to: "/magazine", label: "Magazine" },
    { to: "/community", label: "Community" },
    { to: "/ebooks", label: "E-Books" },
    { to: "/about-us", label: "About" },
    { to: "/contact-us", label: "Contact" },
  ];

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "glass shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        {/* Gradient accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D946EF]/20 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            {/* Logo */}
            <Link
              to="/"
              className="relative flex items-center group"
              data-testid="nav-logo"
            >
              <img
                src="/logo.png"
                alt="PRO PIX3LS"
                className="h-8 md:h-9 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute -inset-2 bg-[#D946EF]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
            </Link>

            {/* Desktop Links */}
            <div className="hidden xl:flex items-center gap-1">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className="relative px-3 py-2 group"
                >
                  <span
                    className={`font-['Manrope'] text-[13px] font-semibold tracking-wide uppercase transition-colors duration-200 ${
                      isActive(link.to)
                        ? "text-white"
                        : "text-zinc-500 group-hover:text-zinc-200"
                    }`}
                  >
                    {link.label}
                  </span>
                  {/* Active / hover indicator */}
                  <span
                    className={`absolute bottom-0 left-3 right-3 h-[2px] rounded-full transition-all duration-300 ${
                      isActive(link.to)
                        ? "bg-[#D946EF] opacity-100"
                        : "bg-[#D946EF] opacity-0 scale-x-0 group-hover:opacity-60 group-hover:scale-x-100"
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="hidden xl:flex items-center gap-3">
              <CurrencyPicker />

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 text-zinc-400 hover:text-white hover:bg-white/[0.04] rounded-full pl-1.5 pr-3 h-9"
                      data-testid="user-menu-trigger"
                    >
                      {user.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover ring-2 ring-[#D946EF]/30 ring-offset-1 ring-offset-black"
                          data-testid="nav-profile-picture"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D946EF] to-[#7C3AED] flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      <span className="font-['Manrope'] text-sm font-medium">
                        {user.first_name}
                      </span>
                      <ChevronDown className="w-3 h-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-48 bg-[#0A0A0A]/95 backdrop-blur-xl border-zinc-800/80 rounded-xl p-1.5 shadow-xl shadow-black/40 animate-scale-in"
                  >
                    {(user.role === "admin" || user.is_staff) && (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate("/admin")}
                          className="text-zinc-400 hover:text-white focus:text-white focus:bg-white/[0.04] cursor-pointer rounded-lg px-3 py-2.5 font-['Manrope'] text-sm"
                          data-testid="nav-admin-link"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2.5 opacity-60" /> Admin
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-zinc-800/60 my-1" />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard")}
                      className="text-zinc-400 hover:text-white focus:text-white focus:bg-white/[0.04] cursor-pointer rounded-lg px-3 py-2.5 font-['Manrope'] text-sm"
                      data-testid="nav-dashboard-link"
                    >
                      <User className="w-4 h-4 mr-2.5 opacity-60" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800/60 my-1" />
                    <DropdownMenuItem
                      onClick={() => { logout(); navigate("/"); }}
                      className="text-zinc-400 hover:text-red-400 focus:text-red-400 focus:bg-red-500/5 cursor-pointer rounded-lg px-3 py-2.5 font-['Manrope'] text-sm"
                      data-testid="nav-logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2.5 opacity-60" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => navigate("/auth")}
                  className="bg-gradient-to-r from-[#D946EF] to-[#C026D3] hover:from-[#C026D3] hover:to-[#A21CAF] text-white font-['Manrope'] text-sm font-semibold px-6 py-2 rounded-full shadow-lg shadow-[#D946EF]/20 hover:shadow-[#D946EF]/30 transition-all duration-300"
                  data-testid="nav-login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile toggle */}
            <button
              className="xl:hidden relative w-10 h-10 flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              <div className="absolute inset-0 rounded-lg hover:bg-white/[0.04] transition-colors" />
              {mobileOpen ? <X className="w-5 h-5 relative z-10" /> : <Menu className="w-5 h-5 relative z-10" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu — full-screen overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 xl:hidden"
          data-testid="mobile-menu"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-[#030303]/98 backdrop-blur-xl overflow-y-auto animate-fade-in-down">
            <div className="max-w-lg mx-auto px-6 py-8 flex flex-col min-h-full">
              {/* Nav links */}
              <div className="flex flex-col gap-1">
                {links.map((link, i) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center py-3 px-4 rounded-xl transition-all duration-200 ${
                      isActive(link.to)
                        ? "bg-[#D946EF]/10 text-white"
                        : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                    }`}
                    style={{ animationDelay: `${i * 0.03}s` }}
                  >
                    <span className="font-['Manrope'] text-[15px] font-semibold tracking-wide uppercase">
                      {link.label}
                    </span>
                    {isActive(link.to) && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#D946EF]" />
                    )}
                  </Link>
                ))}
              </div>

              {/* Divider */}
              <div className="my-6 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

              {/* Currency */}
              <div className="px-4 mb-4">
                <CurrencyPicker align="left" />
              </div>

              {/* User section */}
              <div className="mt-auto pb-8">
                {user ? (
                  <div className="space-y-2">
                    {/* User info */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white/[0.02] rounded-xl mb-3">
                      {user.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-[#D946EF]/30"
                          data-testid="mobile-profile-picture"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D946EF] to-[#7C3AED] flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-['Manrope'] text-sm font-semibold">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-zinc-500 font-['Manrope'] text-xs">
                          {user.email}
                        </p>
                      </div>
                    </div>

                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/[0.03] rounded-xl transition-all"
                    >
                      <User className="w-4 h-4 opacity-50" />
                      <span className="font-['Manrope'] text-sm font-medium">Dashboard</span>
                    </Link>

                    {(user.role === "admin" || user.is_staff) && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/[0.03] rounded-xl transition-all"
                      >
                        <LayoutDashboard className="w-4 h-4 opacity-50" />
                        <span className="font-['Manrope'] text-sm font-medium">Admin</span>
                      </Link>
                    )}

                    <button
                      onClick={() => { logout(); setMobileOpen(false); navigate("/"); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-zinc-500 hover:text-red-400 hover:bg-red-500/5 rounded-xl transition-all text-left"
                    >
                      <LogOut className="w-4 h-4 opacity-50" />
                      <span className="font-['Manrope'] text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                ) : (
                  <div className="px-4">
                    <Button
                      onClick={() => { setMobileOpen(false); navigate("/auth"); }}
                      className="w-full bg-gradient-to-r from-[#D946EF] to-[#C026D3] hover:from-[#C026D3] hover:to-[#A21CAF] text-white font-['Manrope'] text-sm font-semibold py-3 rounded-full shadow-lg shadow-[#D946EF]/20"
                    >
                      Sign In
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
